import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '#client/components/ui/Button';
import { Checkbox } from '#client/components/ui/Checkbox';

interface OrderedListProps<T> {
  items: readonly T[];
  onChange: (items: T[]) => void;
  keyOf: (item: T) => string;
  labelOf: (item: T) => string;
  enabledOf: (item: T) => boolean;
  setEnabled: (item: T, enabled: boolean) => T;
  /** Extra controls for one row — a width, an alignment, a printed label. */
  detail?: ((item: T, replace: (next: T) => void) => ReactNode) | undefined;
}

/**
 * A list whose order and membership are both part of the template.
 *
 * Two things need this: the patient fields on the letterhead and the columns of
 * the results table. In both cases the order *is* the layout — the first four
 * patient fields fill the left column of the identity grid, and column order is
 * left-to-right on the page — so a plain set of checkboxes would leave half the
 * decision unexpressible.
 *
 * Reordering is by button rather than by drag. Buttons are operable by keyboard
 * and on a touch screen without a library, they cannot be started accidentally
 * while scrolling a long panel, and the lists are five to ten items long, where
 * "move it up one" is the whole interaction anyway.
 */
export function OrderedList<T>({
  items,
  onChange,
  keyOf,
  labelOf,
  enabledOf,
  setEnabled,
  detail,
}: OrderedListProps<T>) {
  function move(index: number, delta: number) {
    const target = index + delta;
    const next = [...items];
    const moved = next[index];
    const displaced = next[target];
    if (!moved || !displaced) return;
    next[index] = displaced;
    next[target] = moved;
    onChange(next);
  }

  function replaceAt(index: number, item: T) {
    onChange(items.map((current, position) => (position === index ? item : current)));
  }

  return (
    <ul className="c-ordered">
      {items.map((item, index) => (
        <li className="c-ordered__item" key={keyOf(item)}>
          <div className="c-ordered__row">
            <Checkbox
              label={labelOf(item)}
              checked={enabledOf(item)}
              onChange={(event) => replaceAt(index, setEnabled(item, event.target.checked))}
            />

            <div className="c-ordered__move">
              <Button
                size="sm"
                icon={<ChevronUp />}
                aria-label={`Move ${labelOf(item)} up`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              />
              <Button
                size="sm"
                icon={<ChevronDown />}
                aria-label={`Move ${labelOf(item)} down`}
                disabled={index === items.length - 1}
                onClick={() => move(index, 1)}
              />
            </div>
          </div>

          {/* Only for the rows that are printed: settings on a hidden field are
              controls that do nothing, which is worse than absent ones. */}
          {detail && enabledOf(item) && (
            <div className="c-ordered__detail">
              {detail(item, (next) => replaceAt(index, next))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
